import {
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  useDisclosure,
  Flex,
  Grid,
  GridItem,
  Textarea,
  Text,
  useToast,
  Box,
} from '@chakra-ui/react'
import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Row } from './Row'
import { formatPrice } from '../../../../../../utils/formatPrice'
import { ArrowDownIcon } from '@chakra-ui/icons'
import { Button } from '../../../../../../components/Button'
import { api } from '../../../../../../services/api'
import { useMutation, useQuery } from 'react-query'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUpdateNegociacao } from '../../../../../../hooks/useUpdateNegociacao'
import { useUser } from '../../../../../../hooks/useUser'
import { Produto } from './Produto'
import { TotalDeNegociacoes } from './TotalDeNegociacoes'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

const numericWithCommaRegex = /^\d*(,\d*)?$/
const messageError = 'Apenas números e, no máximo, uma vírgula são permitidos'

const schema = z.object({
  quantidade: z.string().regex(numericWithCommaRegex).optional(),
  preco_venda: z.string().regex(numericWithCommaRegex, messageError).optional(),
  custo_unitario_importador: z.string().regex(numericWithCommaRegex, messageError).optional(),
  custo_unit_impressao: z.string().regex(numericWithCommaRegex, messageError).optional(),
  porcentagem_comissao: z.string().regex(numericWithCommaRegex, messageError).optional(),
  adicional_1: z.string().regex(numericWithCommaRegex, messageError).optional(),
  add_vendas_demissao: z.string().regex(numericWithCommaRegex, messageError).optional(),
  boleto: z.string().regex(numericWithCommaRegex, messageError).optional(),
  frete_compra: z.string().regex(numericWithCommaRegex, messageError).optional(),
  outros_custos_unit: z.string().regex(numericWithCommaRegex, messageError).optional(),
})

async function onGetDadosNegociacao({ codigo_produto, sequencia, numero_nota }) {
  const res = await api.get('/site/v2/orcamento-crm/listar-dados-negociacao', {
    params: {
      codigo_produto,
      sequencia,
      numero_nota,
    },
  })

  return res.data
}

async function onEnviarNegociacao({ numero_nota, codigo_produto, sequencia, ...data }) {
  const res = await api.get('/site/v2/orcamento-crm/envia-negociacao-pedido', {
    params: {
      numero_nota,
      codigo_produto,
      sequencia,
      ...data,
    },
  })

  return res.data
}

export function Negociacao({ item }) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  //pega o codprod e sequencia do params

  const [searchParams, setSearchParams] = useSearchParams()

  const params = useMemo(() => {
    return Object.fromEntries(searchParams)
  }, [searchParams])

  const btnRef = React.useRef()
  const { numeroProposta: numProposta } = useParams()
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  })

  console.log(errors, 'errors')

  const { codigo_usuario } = useUser()

  const toast = useToast({
    position: 'top-right',
  })

  const isAvailable = [30, 21, 14]

  const { data } = useQuery({
    queryKey: ['negociacao-historico', item.codigo_produto, item.sequencia, item.numero_nota],
    queryFn: () => onGetDadosNegociacao({ codigo_produto: item.codigo_produto, sequencia: item.sequencia, numero_nota: item.numero_nota }),
    staleTime: 60 * 1000 * 30,
    refetchOnWindowFocus: false,
  })

  const mutation = useUpdateNegociacao()

  const { mutateAsync: onEnviarNegociacaoH, isLoading: isLoadingEnviarNegociacao } = useMutation({
    mutationFn: onEnviarNegociacao,
    onSuccess: () => {
      toast({
        title: 'Negociação enviada com sucesso',
        status: 'success',
      })
    },
    onError: (error) => {
      toast({
        title: 'Erro ao enviar negociação',
        description: error?.response?.data?.message,
        status: 'error',
      })
    },
  })

  async function handleEnviar() {
    const params = {
      numero_nota: item.numero_nota,
      codigo_produto: item.codigo_produto,
      sequencia: item.sequencia,
    }

    await onEnviarNegociacaoH(params)
  }

  async function onSubmit(data) {
    const params = {
      numero_nota: item.numero_nota,
      codigo_produto: item.codigo_produto,
      sequencia: item.sequencia,
      ...data,
    }

    mutation.mutateAsync(params)
  }

  const [verMais, setVerMais] = React.useState(false)

  useEffect(() => {
    setValue('quantidade', String(data?.negociacao_atual?.quantidade ?? ''))
    setValue('preco_venda', String(data?.negociacao_atual?.preco_venda ?? ''))
    setValue('custo_unit_impressao', String(data?.negociacao_atual?.custo_unit_impressao ?? ''))
    setValue('porcentagem_comissao', String(data?.negociacao_atual?.porcentagem_comissao ?? ''))
    setValue('boleto', String(data?.negociacao_atual?.boleto ?? ''))
    setValue('frete_compra', String(data?.negociacao_atual?.frete_compra ?? ''))
    setValue('outros_custos_unit', String(data?.negociacao_atual?.outros_custos_unit ?? ''))
    setValue('porcentagem_difal', String(data?.negociacao_atual?.porcentagem_difal ?? ''))
    setValue('observacoes', String(data?.negociacao_atual?.observacoes ?? ''))
  }, [setValue, data])

  useEffect(() => {
    if (params.codprod === String(item.codigo_produto) && params.sequencia === String(item.sequencia)) {
      onOpen()
    }
  }, [params, item, onOpen])

  if (!isAvailable.includes(codigo_usuario)) {
    return null
  }

  if (!item.negociacao) {
    return null
  }

  return (
    <>
      <Flex
        alignItems={'center'}
        pointerEvents={'auto'}
        justifyContent={'center'}
        px={4}
        transition="filter 0.3s"
        fontWeight="semibold"
        bgColor="transparent"
        fontSize="13px"
        as="label"
        size="sm"
        color="#2D2D2D"
        cursor="pointer"
        _hover={{ filter: 'brightness(0.9)', bgColor: '#d3d3d3' }}
        onClick={onOpen}
      >
        Negociação
      </Flex>
      <Drawer isOpen={isOpen} onClose={onClose} finalFocusRef={btnRef} placement="bottom">
        <DrawerOverlay />
        <DrawerContent minH="100vh">
          <DrawerBody maxW="1366px" px="16px" w="100%" mx="auto" display="flex" flexDir="column" alignItems="center" justifyContent="center" gap={10}>
            <Grid gridTemplateColumns="275px 400px 1fr" w="full" columnGap={2}>
              <Flex position="relative" flexDir="column" gap={2} justifyContent="space-between">
                <Flex alignItems="center" gap={2}>
                  <Text color="#378BBF">NEGOCIAÇÃO: </Text> <strong>{data?.negociacao_atual?.numero_pedido}</strong>
                </Flex>
                {<Produto {...data?.produto} />}

                <Box position="relative">
                  <TotalDeNegociacoes {...data} verMais={verMais} setVerMais={setVerMais} />
                </Box>
              </Flex>
              <GridItem experimental_spaceY={2}>
                <Flex alignItems="center" gap={2}>
                  <Text color="#378BBF">FORNECEDOR: </Text> <strong>{data?.produto?.nome_fornecedor}</strong>
                </Flex>
                <Flex alignItems="center" gap={2}>
                  <Text color="#378BBF">COD FORNECEDOR: </Text> <strong>{data?.produto?.codigo_fornecedor}</strong>
                </Flex>
                <Flex flexDir="column" gap="0.5">
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>QUANTIDADE</p>
                    </Row.Title>
                    <Row.Input {...register('quantidade')} />
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>PREÇO | VENDA</p>
                    </Row.Title>
                    <Row.Input {...register('preco_venda')} />
                  </Row.Root>
                </Flex>
                <Flex flexDir="column" gap="0.5">
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        CUSTO <strong>UNITÁRIO IMPORTADOR</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.custo_unitario_importador)}</Row.Value>
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        CUSTO <strong>TOTAL IMPORTADOR</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.custo_total_importador)}</Row.Value>
                  </Row.Root>
                </Flex>

                <Flex flexDir="column" gap="0.5">
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        VALOR <strong>UNITÁRIO DIFAL</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.valor_unit_difal)}</Row.Value>
                    <Row.Input {...register('porcentagem_difal')} />
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        VALOR <strong>TOTAL DIFAL</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.valor_total_difal)}</Row.Value>
                  </Row.Root>
                </Flex>

                <Flex flexDir="column" gap="0.5">
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        VALOR <strong>UNITÁRIO IMP + DIFAL</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.valor_unit_impdifal)}</Row.Value>
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        VALOR <strong>TOTAL IMP + DIFAL</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.valor_tot_impdifal)}</Row.Value>
                  </Row.Root>
                </Flex>

                <Flex flexDir="column" gap="0.5">
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        CUSTO <strong>UNITÁRIO IMPRESSÃO</strong>
                      </p>
                    </Row.Title>
                    <Row.Input {...register('custo_unit_impressao')} />
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        CUSTO <strong>TOTAL IMPRESSÃO</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.custo_tot_impressao)}</Row.Value>
                  </Row.Root>
                </Flex>

                <Flex flexDir="column" gap="0.5">
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        VALOR <strong>COMISSÃO</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.valor_comissao)}</Row.Value>
                    <Row.Input {...register('porcentagem_comissao')} />
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        <strong>ADICIONAL 1</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.adicional_1)}</Row.Value>
                    <Row.Porcentagem>{data?.negociacao_atual?.porcentagem_adicional}%</Row.Porcentagem>
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        <strong>LOJA STORE</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.loja_store)}</Row.Value>
                    <Row.Porcentagem>{data?.negociacao_atual?.porcentagem_loja_store}%</Row.Porcentagem>
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        ADD <strong>VENDAS DEMISSÃO</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.add_vendas_demissao)}</Row.Value>
                    <Row.Porcentagem>{data?.negociacao_atual?.porcentagem_add_vendas}%</Row.Porcentagem>
                  </Row.Root>
                  <Row.Root gridTemplateColumns="1fr 100px 55px">
                    <Row.Title>
                      <p>
                        CUSTO <strong>TOTAL DE IMPOSTOS</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{formatPrice(data?.negociacao_atual?.custo_total_imposto)}</Row.Value>
                    <Row.Porcentagem>{data?.negociacao_atual?.porcentagem_imposto}%</Row.Porcentagem>
                  </Row.Root>
                </Flex>
              </GridItem>
              {/* TASKS: Boleto */}
              <Grid colSpan="2" gridTemplateColumns={'300px 1fr'} columnGap={2}>
                <GridItem mt="8" experimental_spaceY={2}>
                  <Flex flexDir="column" gap="0.5">
                    <Row.Root>
                      <Row.Title>
                        <p>BOLETO</p>
                      </Row.Title>
                      <Row.Input {...register('boleto')} />
                    </Row.Root>
                    <Row.Root>
                      <Row.Title>
                        <p>FRETE PARA COMPRA</p>
                      </Row.Title>
                      <Row.Input {...register('frete_compra')} />
                    </Row.Root>
                  </Flex>

                  <Flex flexDir="column" gap="0.5">
                    <Row.Root>
                      <Row.Title>
                        <p>OUTROS CUSTOS UNITÁRIO</p>
                      </Row.Title>
                      <Row.Input {...register('outros_custos_unit')} />
                    </Row.Root>
                    <Row.Root>
                      <Row.Title>
                        <p>TOTAL OUTROS CUSTOS</p>
                      </Row.Title>
                      <Row.Value>{formatPrice(data?.negociacao_atual?.total_outros_custos)}</Row.Value>
                    </Row.Root>
                  </Flex>

                  <Flex flexDir="column" gap="0.5">
                    <Row.Root>
                      <Row.Title>
                        <p>CAIXA UTILIZADA</p>
                      </Row.Title>
                      <Row.Value>{data?.negociacao_atual?.caixa_utilizada}</Row.Value>
                    </Row.Root>
                    <Row.Root>
                      <Row.Title>
                        <p>VALOR DA CAIXA UTILIZADA</p>
                      </Row.Title>
                      <Row.Value>{formatPrice(data?.negociacao_atual?.valor_caixa)}</Row.Value>
                    </Row.Root>
                    <Row.Root>
                      <Row.Title>
                        <p>QUANTIDADE POR CAIXA</p>
                      </Row.Title>
                      <Row.Value>{data?.negociacao_atual?.qtd_porcaixa}</Row.Value>
                    </Row.Root>
                    <Row.Root>
                      <Row.Title>
                        <p>CAIXAS UTILIZADAS</p>
                      </Row.Title>
                      <Row.Value>{data?.negociacao_atual?.caixas_utlizadas}</Row.Value>
                    </Row.Root>
                    <Row.Root>
                      <Row.Title>
                        <p>VALOR DE CAIXAS TOTAL</p>
                      </Row.Title>
                      <Row.Value>{formatPrice(data?.negociacao_atual?.valor_total_caixa)}</Row.Value>
                    </Row.Root>
                  </Flex>
                </GridItem>

                <GridItem mt="8" experimental_spaceY={2}>
                  <Row.Root>
                    <Row.Title color="white" bgColor="#378BBF">
                      <p>CUSTO TOTAL</p>
                    </Row.Title>
                    <Row.Value color="white" bgColor="#378BBF">
                      {formatPrice(data?.negociacao_atual?.custo_total)}
                    </Row.Value>
                  </Row.Root>
                  <Row.Root>
                    <Row.Title color="white" bgColor="#378BBF">
                      <p>VALOR VENDA TOTAL INNOVATION</p>
                    </Row.Title>
                    <Row.Value color="white" bgColor="#378BBF">
                      {formatPrice(data?.negociacao_atual?.valor_venda_total)}
                    </Row.Value>
                  </Row.Root>

                  <Flex flexDir="column" gap="0.5">
                    <Row.Root>
                      <Row.Title color="white" bgColor="#DCAE5E">
                        <p>LUCRO LÍQUIDO BRUTO</p>
                      </Row.Title>
                      <Row.Value color="white" bgColor="#DCAE5E">
                        {formatPrice(data?.negociacao_atual?.lucro_liquido_bruto)}
                      </Row.Value>
                    </Row.Root>
                    <Row.Root>
                      <Row.Title>
                        <p>PORCENTAGEM</p>
                      </Row.Title>
                      <Row.Value>{data?.negociacao_atual?.porcentagem}%</Row.Value>
                    </Row.Root>
                  </Flex>
                  <Row.Root>
                    <Row.Title>
                      <p>
                        PORCENTAGEM <strong>MARKUP</strong>
                      </p>
                    </Row.Title>
                    <Row.Value>{data?.negociacao_atual?.porcentagem_markup}%</Row.Value>
                  </Row.Root>
                </GridItem>

                <GridItem colSpan="2" mt="auto">
                  <Flex flexDir="column" w="100%" alignItems="center" justifyContent="center">
                    <Text fontWeight="semibold" fontSize="12px" color="#378BBF" py={'6px'} textAlign="end" w="100%">
                      OBSERVAÇÕES
                    </Text>
                    <Textarea
                      isReadOnly
                      size="sm"
                      h="112px"
                      fontSize="12px"
                      resize={'none'}
                      lineHeight="20px"
                      letterSpacing="0.6px"
                      bgColor={'white'}
                      border="1px solid"
                      _hover={{ borderColor: '#F5F5F5' }}
                      _focus={{ borderColor: '#F5F5F5' }}
                      borderColor={'#939393'}
                      {...register('observacoes')}
                    />
                  </Flex>

                  <Flex alignItems="center" gap={5} justifyContent="center" w="100%" mt={2}>
                    <Button
                      rightIcon={<ArrowDownIcon />}
                      w="100%"
                      bgColor="transparent"
                      color="#378BBF"
                      borderRadius={0}
                      as="a"
                      href={data?.negociacao_atual?.link_canhoto}
                      target="_blank"
                      isDisabled={!data?.negociacao_atual?.link_canhoto}
                    ></Button>
                    <Button
                      maxW="181px"
                      w="100%"
                      fontSize="15px"
                      fontWeight="semibold"
                      bgColor="transparent"
                      color="#FFA200"
                      border="1px solid #FFA200"
                      //fecha modal
                      onClick={onClose}
                    >
                      Fechar
                    </Button>
                    <Button
                      maxW="181px"
                      w="100%"
                      fontSize="15px"
                      fontWeight="semibold"
                      sx={{
                        _disabled: {
                          bgColor: '#F5F5F5',
                          color: '#939393',
                          cursor: 'not-allowed',
                        },
                      }}
                      onClick={handleEnviar}
                      isLoading={isLoadingEnviarNegociacao}
                    >
                      Enviar
                    </Button>
                    <Button
                      maxW="181px"
                      w="100%"
                      fontSize="15px"
                      fontWeight="semibold"
                      sx={{
                        _disabled: {
                          bgColor: '#F5F5F5',
                          color: '#939393',
                          cursor: 'not-allowed',
                        },
                      }}
                      onClick={handleSubmit(onSubmit)}
                      isLoading={mutation.isLoading}
                    >
                      Salvar
                    </Button>
                  </Flex>
                </GridItem>
              </Grid>

              {/* TASKS: total de negociações */}
              {/* <GridItem experimental_spaceY={2} h="full" display="grid" gridTemplateRows="1fr auto">
                <Flex flexDir="column" gap={2}>
                  <Flex alignItems="center" gap={2}>
                    <Text color="#378BBF">TOTAL DE NEGOCIAÇÕES:</Text>
                    <strong>{data?.negociacao_atual?.total_negociacao}</strong>
                  </Flex>
                  <Flex alignItems="center" justifyContent="space-between" gap={2}>
                    <Row.Title bgColor="#378BBF" color="white" w="100%">
                      DATA
                    </Row.Title>
                    <Row.Title bgColor="#378BBF" color="white" w="100%">
                      VALOR UN.
                    </Row.Title>
                    <Row.Title bgColor="#378BBF" color="white" w="100%">
                      % COMISSÃO
                    </Row.Title>
                  </Flex>
                  <Flex flexDir="column" gap={2} maxH="396px" overflowY="auto">
                    {data?.historico_negociacoes?.map((item) => (
                      <Flex gap={2} justifyContent="space-between" key={item.data_negociacao}>
                        <Row.Title w="100%">{format(new Date(item.data_negociacao), 'dd/MM/yyyy')}</Row.Title>
                        <Row.Title w="100%">{item.valor_unitario}</Row.Title>
                        <Row.Title w="100%">{item.comissao}</Row.Title>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
                
              </GridItem> */}
            </Grid>

            {/* TASKS Observacoes */}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}
